/* eslint-disable react-hooks/exhaustive-deps */
import React, { memo, useState, useEffect } from 'react'
import { useExperiences } from 'react-chansey/lib/provider'
import { Waypoint } from 'react-waypoint'
import uuid from 'uuid/v1'

import FilterBanner from '../filter-banner/filter-banner'
import NoResults from '../no-results'
import { CITY_FIBRE_OUTCODES, isProviderOutcode, MS3_OUTCODES } from '../pages/hull/utils/postcode'
import PriceRiseBanner from '~/components/filter-banner/price-rise-banner'
import { getUserIntent } from '~/components/intent/intent'
import { ModalTrigger } from '~/components/modal'
import NoDealsMessage from '~/components/results-callout/no-deals-message'
import RevertToPostcodeResultsMessage from '~/components/results-callout/revert-to-postcode-message'
import VirginMediaBanner from '~/components/results-callout/virgin-media-banner'
import SkeletonDealCard from '~/components/skeletons/deal-cards'
import SmallPrint from '~/components/small-print'
import Table from '~/components/table'
import ActiveFilters from '~/components/table/active-filters'
import { ClearAllFilters } from '~/components/table/clear-all-filters'
import FloatingFilterButton from '~/components/table/floating-filter-button'
import Sidebar from '~/components/table/sidebar'
import Tooltip from '~/components/tooltip'
import { broadbandTypesFormatCityFibreTrial } from '~/config/formatting'
import { broadbandTypesSortCityFibreTrial } from '~/config/sorting'
import { broadbandTypesTooltipTextsCityFibreTrial } from '~/config/tooltip-texts'
import { useAppContext } from '~/contexts/app-context'
import { usePageContext } from '~/contexts/page-context'
import RESULTS_DEALS_AND_ADS_QUERY from '~/graphql/queries/results-deals-and-ads'
import RESULTS_PROVIDER_QUERY from '~/graphql/queries/results-provider'
import { useInBucket } from '~/hooks/use-in-bucket'
import useQueryWithPagination from '~/hooks/useQueryWithPagination'
import { hasPremiseAvailability } from '~/utils/deals-contain-premise-level-availability'
import { notEmpty } from '~/utils/empty'
import getTableVariables from '~/utils/get-table-variables'
import { getProviderNameFromId, nonProviders, PROVIDER_IDS } from '~/utils/providers'
import { PROVIDERS } from '~/utils/providers-listing'
import { track } from '~/utils/tracking'

import styles from './broadband-table-styles.module.css'

const ONE_SECOND_IN_MS = 1000
const AD_P2 = 2
const INTENT_JOURNEY_MIN_DEALS = 5

const BroadbandTable = ({
  address,
  currentProvider,
  adsVariables,
  filters,
  sharedJourneyVariant,
  loading = true,
  onChangeFilters,
  onChangeOrder,
  order,
  postcode,
  providerPage = false,
  nonServiceabilityPage,
  setLoading,
  showResultsSummary = false,
  criticalJs = false,
  showSmallPrint = true,
  personalise = false,
  showAds = true,
  widelyAvailable = false,
  cityFibreDealsShown = false
}) => {
  const { device, comparisonId: contextComparisonId, isServer } = useAppContext()
  const experiences = useExperiences()
  const { filterConfig, location, orderings, resetSlug, slug, params, connectionTypes, previewFlag } = usePageContext()
  const isVirginMediaBanner = useInBucket('experiment-broadband-virgin-media-banner', 'variant')
  const isMs3BannerTest = useInBucket('experiment-trustpilothighlightreview', 'variant')
  const notPriceRise = useInBucket('experiment-broadband-price-rise-2024', 'no-price-rises')

  const isCityFibreTest = useInBucket('experiment-broadband-city-fibre-trial', 'variant')
  const [isCityFibreTrial, setCityFibreTrial] = useState(null)
  const isPostcodeInCityFibreArea = isProviderOutcode(postcode, CITY_FIBRE_OUTCODES)
  const [showBannerButton, setShowBannerButton] = useState(true)

  const [intent, setIntent] = useState(getUserIntent(slug, filters))
  const [isSidebarOpen, setSidebarOpen] = useState(false)
  const [hasSidebarBeenClosed, setHasSidebarBeenClosed] = useState(false)

  const [show10MoreClicked, setShow10MoreClicked] = useState(false)
  const [filtersChanged, setFiltersChanged] = useState(false)
  const [filterButtonIsHidden, setFilterButtonIsHidden] = useState(!criticalJs ? null : true)
  const [showSkeleton, setShowSkeleton] = useState(!criticalJs)

  const pageQuery = providerPage ? RESULTS_PROVIDER_QUERY : RESULTS_DEALS_AND_ADS_QUERY

  const dealsVariables = {
    filterConfig,
    device,
    params,
    slug,
    connectionTypes,
    comparisonId: contextComparisonId,
    previewFlag,
    filters,
    order,
    personalise,
    widelyAvailable,
    algorithmVersion: experiences['experiment-bb-algorithm']?.value
  }

  if (slug === 'fibre_results' && isCityFibreTest && isPostcodeInCityFibreArea) {
    dealsVariables.filterConfig.map((item) => {
      if (item.objKey === 'broadbandTypes') {
        item.broadbandTypeConfig = 'CITY_FIBRE_TRIAL'
        item.formatLabelFn = broadbandTypesFormatCityFibreTrial
        item.sortingFn = broadbandTypesSortCityFibreTrial
        item.tooltipTexts = broadbandTypesTooltipTextsCityFibreTrial
        item.title = 'Connection type'
      }
      return item
    })
  }

  dealsVariables.sjidVariant = sharedJourneyVariant

  const productsPerPage = 10
  const tableVariables = getTableVariables(dealsVariables, productsPerPage, showAds)

  const {
    fetching,
    error,
    data = null,
    fetchMore
  } = useQueryWithPagination({
    query: pageQuery,
    variables: { ...tableVariables, ...adsVariables },
    requestPolicy: 'cache-first',
    pause: !criticalJs && isServer
  })

  if (error && (error.networkError || (data && !data.dealsFeed))) {
    throw error
  }

  useEffect(() => {
    if (!fetching) {
      setLoading(false)
      setShowSkeleton(false)
    }
  }, [loading, filters, order, fetching, setLoading, setShowSkeleton])

  const dealsFeed = data?.dealsFeed || data?.dealsFeedCustomer
  const adsFeed = data?.adsFeed

  useEffect(() => {
    if (!fetching && isCityFibreTrial === null && isCityFibreTest && isPostcodeInCityFibreArea) {
      const dealsAvailable = dealsFeed?.availableFilters?.nextGenTrial.some(
        (option) => option.value === 'CITY_FIBRE_TRIAL' && option.count >= 1
      )
      setCityFibreTrial(dealsAvailable)
    }
  }, [
    loading,
    filters,
    order,
    fetching,
    setLoading,
    setShowSkeleton,
    isCityFibreTest,
    isPostcodeInCityFibreArea,
    isCityFibreTrial,
    dealsFeed,
    address
  ])

  const toggleSidebar = (e) => {
    e.preventDefault()
    document.documentElement.classList.toggle(styles.bodyNoScroll, !isSidebarOpen)

    setShowSkeleton(false)

    if (isSidebarOpen && filtersChanged) {
      setShowSkeleton(true)
      setTimeout(() => {
        setShowSkeleton(false)
      }, ONE_SECOND_IN_MS)
    }

    setFiltersChanged(false)

    setSidebarOpen(!isSidebarOpen)

    // We only want to fire the opened event as the modal closing varies in the experiment
    if (!isSidebarOpen) {
      track('Broadband - Filter & Sort', 'Modal opened')
    } else {
      setHasSidebarBeenClosed(true)
    }
  }

  const changeFiltersHandler = (filters) => {
    setShow10MoreClicked(false)
    onChangeFilters(filters)
    setIntent(getUserIntent(slug, filters))
    setFiltersChanged(true)
  }

  const changeOrderHandler = ({ target: { value } }) => {
    setShow10MoreClicked(false)
    onChangeOrder(value)
    track('Sort By', value)
  }

  const clearFiltersHandler = (filters) => {
    track('Broadband - Deals Table', 'Clear all filters')

    if (resetSlug) resetSlug()
    changeFiltersHandler(filters)
  }

  const getTableTenancies = (tenancies) => {
    // only return in table tenancies
    return tenancies.filter((tenancy) => tenancy.position.position !== 0)
  }

  const onDealsChange = () => {
    window.adInsightCode && window.adInsightCode()
  }

  if (!dealsFeed) {
    return null
  }

  const { count, availableFilters, nextCursor, comparisonId } = dealsFeed

  let { deals } = dealsFeed

  const tableAds = !adsFeed ? [] : getTableTenancies(adsFeed.ads).filter((tenancy) => !!tenancy)
  const providerName = getProviderNameFromId(PROVIDERS.concat(nonProviders), currentProvider)

  const dealsExist = notEmpty(deals)

  if (adsFeed && adsFeed?.ads?.find((ad) => ad.position.position === AD_P2)) {
    const {
      display: { deal }
    } = adsFeed.ads.find((ad) => ad.position.position === AD_P2)
    dealsFeed.deals = dealsFeed.deals.filter((product) => product.productId !== deal.productId)
  }

  const isPostcodeInMs3Area = isProviderOutcode(postcode, MS3_OUTCODES)

  if (isPostcodeInMs3Area) {
    // Remove mobile deals from the table - this is so we don't show mobile deals in MS3 areas
    deals = dealsFeed.deals.filter((deal) => deal.services.broadband?.connectionType !== 'Mobile')
  }

  const ms3DealsAvailable = deals.some((deal) => deal?.services?.broadband?.networkName === 'ms3')

  const showMs3Banner = isMs3BannerTest && ms3DealsAvailable && isPostcodeInMs3Area

  const missingPremiseAvailability = dealsExist && !nonServiceabilityPage && !hasPremiseAvailability(dealsFeed?.deals)

  const selectedFiltersCount = Object.values(filters).flat().length

  const sortedBy = orderings.find((o) => o.sortKey === order)

  const sortedByLabel = sortedBy.description
  const sortedByTooltip = sortedBy?.tooltip

  const showVirginMediaBanner = isVirginMediaBanner && currentProvider === PROVIDER_IDS.VIRGIN

  if (criticalJs && !providerPage && deals.length < INTENT_JOURNEY_MIN_DEALS) {
    return <div id='table' />
  }

  const totalDeals = (widelyAvailable && data?.allDealsFeed?.count) || count

  return (
    <div className={styles.broadbandTable}>
      {deals.length > 0 && (
        <ModalTrigger
          condition={criticalJs}
          id='postcode'
          data-trigger-source-id-param={uuid()}
          data-trigger-source-param='sidebarFiltersModal'
        >
          <Sidebar
            filterConfig={filterConfig}
            availableFilters={availableFilters}
            selectedFilters={filters}
            onChangeFilters={changeFiltersHandler}
            order={order}
            onChangeOrder={changeOrderHandler}
            availableDealsCount={totalDeals}
            intent={intent}
            isSidebarOpen={isSidebarOpen}
            toggleSidebar={toggleSidebar}
            loading={loading}
            deals={deals}
            tableAds={tableAds}
            setShowBannerButton={setShowBannerButton}
          />
        </ModalTrigger>
      )}

      {filterButtonIsHidden !== null && totalDeals > 0 && (
        <ModalTrigger
          condition={criticalJs}
          id='postcode'
          data-trigger-source-id-param={uuid()}
          data-trigger-source-param='floatingFilterButtonModal'
        >
          <FloatingFilterButton
            onClick={toggleSidebar}
            isCollapsed={hasSidebarBeenClosed}
            filtersCount={selectedFiltersCount}
            isHidden={criticalJs || filterButtonIsHidden}
          />
        </ModalTrigger>
      )}

      <div className={styles.main}>
        {isPostcodeInMs3Area && !deals.length && <NoResults address={address} currentProvider={currentProvider} />}

        <VirginMediaBanner trackingProps={{ comparisonId, postcode, sort: order }} visibility={showVirginMediaBanner} />

        {!showVirginMediaBanner && !isCityFibreTrial && !showMs3Banner && (
          <PriceRiseBanner currentProvider={currentProvider} setFilter={onChangeFilters} />
        )}

        <RevertToPostcodeResultsMessage visibility={missingPremiseAvailability} />

        <NoDealsMessage setFilters={onChangeFilters} visible={!dealsExist && !loading && !isPostcodeInMs3Area} />

        <FilterBanner
          postcode={postcode}
          setFilter={onChangeFilters}
          isCityFibreTrial={isCityFibreTrial}
          showMs3Banner={showMs3Banner}
          forceIntersection={cityFibreDealsShown}
          showBannerButton={showBannerButton}
          setShowBannerButton={setShowBannerButton}
        />

        <div className={styles.tableHeaderWrapper} data-is-sidebar-open={isSidebarOpen}>
          {totalDeals > 0 && (
            <>
              <div id='table-header' className={styles.tableHeader}>
                {showResultsSummary && (
                  <div className={styles.dealsCount}>
                    {`${deals.length + tableAds.length} of ${totalDeals + tableAds.length} results`}
                  </div>
                )}

                {notEmpty(filters) && (
                  <ClearAllFilters
                    selectedFilters={filters}
                    filterConfig={filterConfig}
                    onClearFilters={clearFiltersHandler}
                    setShowBannerButton={setShowBannerButton}
                  />
                )}
              </div>

              {(notEmpty(filters) || resetSlug) && (
                <div className={styles.activeFilters}>
                  <ActiveFilters
                    allFilters={availableFilters}
                    selectedFilters={filters}
                    onChange={changeFiltersHandler}
                    setShowBannerButton={setShowBannerButton}
                  />
                </div>
              )}

              {showResultsSummary && (
                <div className={styles.sortedBy}>
                  Sorted by: <span className={styles.sortedByLabel}>{sortedByLabel}</span>
                  {!!sortedByTooltip && (
                    <Tooltip
                      content={sortedByTooltip}
                      wrapperStyles={styles.tooltipWrapper}
                      iconStyles={styles.tooltipIconStyles}
                      position='below'
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <Waypoint
          onEnter={() => setFilterButtonIsHidden(false)}
          onLeave={() => setFilterButtonIsHidden(true)}
          topOffset='40%'
          bottomOffset='40%'
        >
          <div
            id='table'
            className={styles.table}
            data-show-price-rise-banner={`${(!!isCityFibreTrial || !!showMs3Banner) && !notPriceRise}`}
          >
            {showSkeleton && (
              <div className={styles.skeletonDeals}>
                <SkeletonDealCard amount={3} />
              </div>
            )}

            <Table
              isPostcodeInMs3Area={isPostcodeInMs3Area}
              loading={loading}
              tenancies={tableAds}
              deals={deals}
              comparisonId={comparisonId}
              postcode={postcode}
              address={address}
              sortBy={order}
              currentProvider={providerName}
              hasNextPage={!criticalJs && !!nextCursor}
              ecommerceData={{
                dimension94: providerName ? `broadband:${providerName}` : undefined,
                page: location.pathname
              }}
              inputVariables={tableVariables}
              onDealsChange={onDealsChange}
              paginate={() => fetchMore(nextCursor)}
              dealsCount={totalDeals}
              setShow10MoreClicked={setShow10MoreClicked}
              show10MoreClicked={show10MoreClicked}
              intent={intent}
              criticalJs={criticalJs}
              setFilter={onChangeFilters}
              currentProviderId={currentProvider}
              cityFibreDealsAvailable={dealsFeed?.availableFilters?.nextGenTrial.some(
                (option) => option.value === 'CITY_FIBRE_TRIAL' && option.count >= 1
              )}
            />
          </div>
        </Waypoint>
        {showSmallPrint && <SmallPrint />}
      </div>
    </div>
  )
}

const areEqual = (prevProps, nextProps) =>
  JSON.stringify(prevProps.address) === JSON.stringify(nextProps.address) &&
  prevProps.currentProvider === nextProps.currentProvider &&
  prevProps.loading === nextProps.loading

export default memo(BroadbandTable, areEqual)
